import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { get, post } from "../../lib/http";
import { API, API_BASE_ORIGIN } from "../../config/api";
import { toast } from "react-toastify";
import { offlineBookingsPath, readRememberedEventId } from "../../organizer/eventContext.js";
import { formatEventDateTime } from "../../utils/datetime.js";

const fmtInr = (n = 0) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="min-w-[92px] text-slate-600">{label}</span>
      <span className="break-words text-slate-900">{children || "-"}</span>
    </div>
  );
}

function Badge({ tone = "slate", children }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 ring-slate-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    red: "bg-rose-50 text-rose-700 ring-rose-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ring-1 ${tones[tone]}`}>
      {children}
    </span>
  );
}

export default function OrganizerViewBooking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");

  const assignee = useMemo(() => {
    return booking?.assignedBy || payment?.issuedBy || tickets?.issuedBy || null;
  }, [booking, payment, tickets]);

  useEffect(() => {
    let on = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        try {
          const full = await get(`${API.booking.getAdminBookingFull}/${id}`);
          if (full?.booking || full?.tickets || full?.payment) {
            if (!on) return;
            setBooking(full.booking || null);
            setTickets(full.tickets || null);
            setPayment(full.payment || null);
            return;
          }
        } catch {
          /* fallback */
        }
        const [bRes, tRes, pRes] = await Promise.all([
          get(`${API.booking.getAdminBookingById}/${id}`),
          get(`${API.booking.getAdminTicketsByBooking}/${id}`),
          get(`${API.booking.getAdminPaymentByBooking}/${id}`),
        ]);
        if (!on) return;
        setBooking(bRes?.booking || bRes || null);
        setTickets(tRes?.tickets || tRes || null);
        setPayment(pRes?.payment || pRes || null);
      } catch (e) {
        if (!on) return;
        const msg = e?.message || "Failed to load booking";
        setError(msg);
        toast.error(msg);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [id]);

  const totalTickets = useMemo(() => {
    return (tickets?.ticketCategories || []).reduce(
      (s, c) => s + Number(c.finalQuantity || 0),
      0
    );
  }, [tickets]);

  const onDownloadPDF = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const resp = await fetch(`${API_BASE_ORIGIN}${API.booking.getAdminBookingPDF}/${id}`, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/pdf",
        },
      });
      const ct = resp.headers.get("content-type") || "";
      if (!resp.ok) {
        if (ct.includes("application/json")) {
          const errJson = await resp.json().catch(() => ({}));
          throw new Error(errJson?.message || "Failed to generate PDF");
        }
        throw new Error("Failed to generate PDF");
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ticket_${booking?.bookingCode || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.message || "Failed to download PDF");
    }
  };

  const onResendEmail = async () => {
    try {
      await post(`${API.booking.postAdminBookingResendEmail}/${id}`, {});
      toast.success("Email queued to buyer.");
    } catch (e) {
      toast.error(e?.message || "Failed to resend email");
    }
  };

  if (loading) return <div className="p-2 text-sm text-slate-600">Loading…</div>;
  if (error) return <div className="p-2 text-sm text-rose-600">{error}</div>;
  if (!booking) return <div className="p-2 text-sm text-slate-600">Not found.</div>;

  const eventName = booking?.eventId?.name || "-";
  const eventDate = booking?.eventId?.date ? formatEventDateTime(booking.eventId.date) : "-";
  const venue = booking?.eventId?.venue?.name || "-";
  const buyer = booking?.userId || payment?.payer || tickets?.buyer || {};
  const payMethod = payment?.paymentMethod || booking?.paymentMode || "-";
  const payStatus = payment?.paymentStatus || booking?.paymentStatus || "-";
  const amount = payment?.finalAmount ?? booking?.finalAmount ?? 0;
  const isFree = Number(amount) === 0 || String(payMethod).toUpperCase() === "FREE";
  const bookingEventId =
    booking?.eventId?._id || booking?.eventId?.id || (typeof booking?.eventId === "string" ? booking.eventId : "") || readRememberedEventId();

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase text-amber-700">Booking</div>
          <h1 className="text-xl font-extrabold text-slate-900 sm:text-2xl">Booking details</h1>
          <div className="mt-1 text-sm text-slate-500">
            {booking?.bookingCode ? <>Code: <b>{booking.bookingCode}</b></> : null}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Badge tone={payStatus === "Paid" || payStatus === "Success" ? "green" : "slate"}>
              {payStatus}
            </Badge>
            <Badge tone={isFree ? "green" : "amber"}>{isFree ? "Offline mode" : payMethod}</Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={offlineBookingsPath(bookingEventId)}
            state={bookingEventId ? { eventId: bookingEventId } : undefined}
            className="rounded-xl border px-4 py-2 text-sm font-semibold"
          >
            Offline bookings
          </Link>
          <button onClick={() => navigate(-1)} className="rounded-xl border px-4 py-2 text-sm font-semibold">
            Back
          </button>
          <button onClick={onDownloadPDF} className="rounded-xl border px-4 py-2 text-sm font-semibold">
            Download PDF
          </button>
          {buyer?.email && (
            <button
              onClick={onResendEmail}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            >
              Resend email
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-base font-semibold text-slate-900">Event</h3>
          <div className="space-y-1">
            <Row label="Name:">
              <b>{eventName}</b>
            </Row>
            <Row label="Date:">{eventDate}</Row>
            <Row label="Venue:">{venue}</Row>
          </div>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-base font-semibold text-slate-900">Guest</h3>
          <div className="space-y-1">
            <Row label="Name:">{buyer?.name}</Row>
            <Row label="Email:">{buyer?.email}</Row>
            <Row label="Mobile:">{buyer?.mobile}</Row>
          </div>
        </div>
      </div>

      {assignee ? (
        <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
          <h3 className="mb-3 text-base font-semibold text-slate-900">Issued by</h3>
          <div className="grid gap-1 text-sm sm:grid-cols-3">
            <Row label="Name:">{assignee?.name}</Row>
            <Row label="Email:">{assignee?.email}</Row>
            <Row label="Mobile:">{assignee?.mobile}</Row>
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
        <h3 className="mb-3 text-base font-semibold text-slate-900">Payment</h3>
        <div className="grid gap-2 text-sm sm:grid-cols-3">
          <Row label="Method:">
            <b>{isFree ? "Offline mode" : payMethod}</b>
          </Row>
          <Row label="Status:">
            <b>{payStatus}</b>
          </Row>
          <Row label="Amount:">
            <b>{isFree ? "Free" : fmtInr(amount)}</b>
          </Row>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-100 bg-white p-5 shadow-sm">
        <h3 className="mb-3 text-base font-semibold text-slate-900">Tickets ({totalTickets})</h3>
        <div className="space-y-4">
          {(tickets?.ticketCategories || []).map((c) => (
            <div key={c.categoryId} className="rounded-xl border p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">{c.categoryId}</div>
                <div className="text-sm text-slate-600">
                  qty: <b>{c.finalQuantity}</b>
                  {Number(c.freeQuantity) > 0 ? " (offline)" : ""}
                </div>
              </div>
              {!!c.codes?.length && (
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {c.codes.map((code, idx) => (
                    <div key={idx} className="rounded-lg bg-slate-50 px-3 py-2 font-mono text-sm">
                      {code}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {tickets?.qrCode && (
          <div className="mt-4">
            <div className="mb-1 text-xs text-slate-500">QR</div>
            <img alt="QR" src={tickets.qrCode} className="h-32 w-32 object-contain" />
          </div>
        )}
      </div>
    </div>
  );
}
